import axios from "axios";
import { GEMINI_API_KEY } from "../utils/envConfig";
import { calculatePathStockFromPackages } from "../utils/pathItemPricingUtils";
import { resolvePathCriticalLevel } from "../utils/pathLowStock";

const GEMINI_API_URL = "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent";
const API_KEY = GEMINI_API_KEY;

// Process path inventory invoice using OCR
export const processPathInventoryOCR = async (base64Data, fileType = 'image/jpeg', retryCount = 0) => {
  try {
    if (!API_KEY) {
      throw new Error("Gemini API key not configured");
    }

    // Use the provided base64 data directly
    const base64 = base64Data;

    const prompt = `Extract invoice data as JSON. Find ALL items in ALL sections (main + pathology). Use this exact format:

{
  "invoiceNumber": "number if visible",
  "invoiceDate": "YYYY-MM-DD format", 
  "supplier": "company name",
  "totalAmount": number,
  "items": [
    {
      "name": "item description",
      "unitType": "pieces",
      "requiredQuantity": number,
      "cost": number,
      "inventoryQuantity": number,
      "batchNumber": "batch if visible",
      "expiryDate": "YYYY-MM-DD or null",
      "amount": number,
      "sgst": number,
      "cgst": number,
      "discount": number
    }
  ]
}

Rules:
- Extract from ALL tables/sections
- Convert MM/YY to YYYY-MM-DD
- Split "6.00+ 6.00" to sgst:6.00, cgst:6.00
- Use null for missing fields
- Return ONLY valid JSON, no other text`;

    const requestBody = {
      contents: [
        {
          parts: [
            {
              text: prompt
            },
            {
              inline_data: {
                mime_type: fileType,
                data: base64.split(',')[1]
              }
            }
          ]
        }
      ],
      generationConfig: {
        temperature: 0.0,
        topK: 1,
        topP: 0.8,
        maxOutputTokens: 8192, // Increased from 2048 to handle larger invoices
      }
    };

    // Retry logic for handling 503 Service Unavailable errors
    let response;
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds

    while (retryCount < maxRetries) {
      try {
        response = await axios.post(
          `${GEMINI_API_URL}?key=${API_KEY}`,
          requestBody,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        break; // Success, exit retry loop
      } catch (error) {
        if (error.response?.status === 503 && retryCount < maxRetries - 1) {
          retryCount++;
          console.log(`API overloaded (503), retrying in ${retryDelay}ms... (attempt ${retryCount}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * retryCount)); // Exponential backoff
          continue;
        }
        throw error; // Re-throw if not a 503 or max retries reached
      }
    }

    if (response.data && response.data.candidates && response.data.candidates[0]) {
      const extractedText = response.data.candidates[0].content.parts[0].text;
      
      const cleanedText = extractedText.replace(/```json\n?|\n?```/g, '').trim();
      
      try {
        const parsedData = JSON.parse(cleanedText);
        
        const validatedData = validateAndCleanPathOCRData(parsedData);
        
        return validatedData;
      } catch (parseError) {
        console.error("JSON parsing error:", parseError);
        console.error("Failed to parse text:", cleanedText);
        
        // Try to recover from truncated JSON
        const recoveredData = attemptJSONRecovery(cleanedText);
        if (recoveredData) {
          console.log("Successfully recovered JSON data from truncated response");
          const validatedData = validateAndCleanPathOCRData(recoveredData);
          return validatedData;
        }
        
        throw new Error(`Failed to parse OCR response as JSON: ${parseError.message}`);
      }
    } else {
      console.error("Invalid OCR response structure:", response.data);
      throw new Error("No data extracted from the image");
    }

  } catch (error) {
    console.error("OCR processing error:", error);
    
    // Retry logic for temporary failures
    if (retryCount < 2 && (
        error.response?.status === 503 ||
        error.response?.status === 500 ||
        error.response?.status === 429 ||
        error.message.includes("Service Unavailable") ||
        error.message.includes("Internal Server Error") ||
        error.message.includes("Too Many Requests")
    )) {
      console.log(`Retrying OCR request (attempt ${retryCount + 1}/3)...`);
      await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1))); // Exponential backoff
      return processPathInventoryOCR(base64Data, fileType, retryCount + 1);
    }
    
    // Use mock data for development/testing when API is not available
    if (error.message.includes("API key not configured") || 
        error.message.includes("Network Error") || 
        error.response?.status === 404 ||
        error.response?.status === 503 ||
        error.response?.status === 500 ||
        error.response?.status === 429 ||
        error.message.includes("not found") ||
        error.message.includes("Service Unavailable") ||
        error.message.includes("Internal Server Error") ||
        error.message.includes("Too Many Requests")) {
      console.warn("Using mock data for development - API not available or overloaded");
      console.warn("API Error Details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        retryAttempts: retryCount
      });
      return getMockPathOCRData();
    }
    
    throw new Error(`OCR processing failed: ${error.message}`);
  }
};

// Add path inventory items from OCR results
export const addPathInventoryFromOCR = async (ocrData, user = null) => {
  try {
    // Import the existing services
    const { createInventory } = await import('./inventoryService.js');
    const { getBOMsFromBaseTable, createBOMInBaseTable, updateBOMInBaseTable } = await import('./labService.js');
    const { createInvoice } = await import('./invoiceService.js');
    const { recordPathologyStockIn } = await import('./pathologyTransactionService.js');
    
    // Calculate total tax from all items
    const totalSGST = ocrData.items.reduce((sum, item) => sum + (item.sgst || 0), 0);
    const totalCGST = ocrData.items.reduce((sum, item) => sum + (item.cgst || 0), 0);
    const totalAmount = ocrData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxableAmount = totalAmount - ocrData.items.reduce((sum, item) => sum + (item.discount || 0), 0);

    // First, create the invoice
    const invoiceData = {
      invoiceNumber: ocrData.invoiceNumber || `PATH-OCR-${Date.now()}`,
      amount: taxableAmount,
      discount: ocrData.items.reduce((sum, item) => sum + (item.discount || 0), 0),
      sgst: totalSGST,
      cgst: totalCGST,
      totalAmount: ocrData.totalAmount || (taxableAmount + totalSGST + totalCGST),
      date: ocrData.invoiceDate || new Date().toISOString().split('T')[0],
      invoiceType: "PATHOLOGY",
      fromName: ocrData.supplierName || ocrData.fromName || "OCR Import",
      supplierId: ocrData.supplierId || null,
      supplierName: ocrData.supplierName || null,
      paymentMode: (ocrData.paymentMode || "CASH").toString().toUpperCase(),
      creditOrganizationId: ocrData.creditOrganizationId || null,
      particulars: ocrData.particulars || "Pathology stock (OCR import)",
      ...(ocrData.attachment ? { attachment: ocrData.attachment } : {}),
    };
    
    console.log('Creating invoice for OCR data:', invoiceData);
    const createdInvoice = await createInvoice(invoiceData);
    console.log('Successfully created invoice:', createdInvoice);

    // Get all existing BOMs from base table (not organization-specific) to check if items exist
    const existingBOMs = await getBOMsFromBaseTable();
    const bomMap = existingBOMs.reduce((acc, bom) => {
      acc[bom.name.toLowerCase()] = bom;
      return acc;
    }, {});

    // Helper function to find BOM by name (case-insensitive)
    const findBOMByName = (itemName) => {
      const normalizedName = itemName.toLowerCase().trim();
      return bomMap[normalizedName] || null;
    };

    const inventoryPromises = ocrData.items.map(async (item) => {
      try {
        let bomId = null;
        let wasBOMUpdated = false;
        
        // Check if item exists in BOM table
        const existingBOM = findBOMByName(item.name);
        
        if (existingBOM) {
          // BOM exists, update it with new information
          bomId = existingBOM.id;
          
          const updatedBOMData = {
            ...existingBOM,
            unitType: item.unitType || existingBOM.unitType || "Each",
            qtyUnit: item.qtyUnit || existingBOM.qtyUnit || existingBOM.qty_unit || "piece",
            details: item.details ?? existingBOM.details ?? 1,
            requiredQuantity: item.requiredQuantity || existingBOM.requiredQuantity || 1,
            cost: item.cost || existingBOM.cost || 0,
            costPerUnit: item.cost || existingBOM.costPerUnit || 0,
            description:
              existingBOM.description && !String(existingBOM.description).match(/^(Box|Bottle|Packet|Each)(\||$)/i)
                ? existingBOM.description
                : existingBOM.description || `Updated from OCR invoice`,
          };
          
          // Only update if there are actual changes
          const hasChanges = (
            updatedBOMData.unitType !== existingBOM.unitType ||
            updatedBOMData.qtyUnit !== (existingBOM.qtyUnit ?? existingBOM.qty_unit) ||
            updatedBOMData.details !== existingBOM.details ||
            updatedBOMData.requiredQuantity !== existingBOM.requiredQuantity ||
            updatedBOMData.cost !== existingBOM.cost ||
            updatedBOMData.costPerUnit !== existingBOM.costPerUnit
          );
          
          if (hasChanges) {
            // Update BOM in base table (not organization-specific)
            await updateBOMInBaseTable(existingBOM.id, updatedBOMData);
            wasBOMUpdated = true;
            console.log(`Updated existing BOM item: ${item.name} with ID: ${bomId}`);
          } else {
            console.log(`BOM item ${item.name} already up to date, no changes needed`);
          }
        } else {
          // BOM doesn't exist, create it first
          const newBOMData = {
            name: item.name,
            unitType: item.unitType || "Each",
            qtyUnit: item.qtyUnit || "piece",
            details: item.details ?? 1,
            requiredQuantity: item.requiredQuantity || 1,
            cost: item.cost || 0,
            costPerUnit: item.cost || 0,
            description: `Auto-created from OCR invoice`
          };
          
          // Create BOM in base table (not organization-specific)
          const createdBOM = await createBOMInBaseTable(newBOMData);
          bomId = createdBOM.id;
          
          console.log(`Created new BOM item: ${item.name} with ID: ${bomId}`);
        }

        // Create inventory entry for each item
        // inventoryQuantity is package count (bottles/boxes); convert to stock units + total system price
        const packageQty = item.inventoryQuantity || 0;
        const bomForStock = {
          unitType: item.unitType,
          qtyUnit: item.qtyUnit,
          details: item.details,
          cost: item.cost,
        };
        const stockCalc = calculatePathStockFromPackages(packageQty, bomForStock);

        const inventoryData = {
          name: item.name,
          batchNumber: item.batchNumber || "N/A",
          price: stockCalc.price,
          vendorPrice: item.cost || 0,
          quantity: stockCalc.quantity,
          criticalLevel: resolvePathCriticalLevel(item.criticalLevel),
          expirationDate: item.expiryDate || null, // Use extracted expiry date
          userId: user?.id || 1, // Use logged-in user ID or default
          invoiceId: createdInvoice.id, // Link to the created invoice
          bomId: bomId // Link to BOM
        };

        // Call the existing create inventory API
        console.log(`Creating inventory for item: ${item.name}`, inventoryData);
        const result = await createInventory(inventoryData);
        console.log(`Successfully created inventory:`, result);
        recordPathologyStockIn({
          inventoryId: result?.id,
          bomId,
          itemName: item.name,
          batchNumber: item.batchNumber || "N/A",
          unitType: item.unitType || item.qtyUnit || "",
          deductedQuantity: stockCalc.quantity,
          transactionType: "IN",
          invoiceId: createdInvoice.id,
          supplierId: ocrData.supplierId || null,
          supplierName: ocrData.supplierName || ocrData.fromName || "",
          costPerUnit:
            stockCalc.quantity > 0
              ? stockCalc.price / stockCalc.quantity
              : stockCalc.price,
          expiryDate: item.expiryDate || null,
          userId: user?.id || 1,
        });
        
        return {
          success: true,
          item: item.name,
          quantity: item.inventoryQuantity,
          bomId: bomId,
          wasNewBOM: !existingBOM,
          wasBOMUpdated: wasBOMUpdated,
          inventoryId: result.id
        };
      } catch (itemError) {
        console.error(`Error adding item ${item.name}:`, itemError);
        return {
          success: false,
          item: item.name,
          error: itemError.message,
          details: itemError.response?.data || itemError.message
        };
      }
    });

    const results = await Promise.all(inventoryPromises);
    
    const successful = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);
    const newBOMsCreated = successful.filter(r => r.wasNewBOM);
    const updatedBOMs = successful.filter(r => r.wasBOMUpdated);
    
    return {
      success: failed.length === 0,
      message: `Successfully created invoice ${createdInvoice.invoiceNumber} and added ${successful.length} items to inventory${failed.length > 0 ? `, ${failed.length} failed` : ''}${newBOMsCreated.length > 0 ? `, ${newBOMsCreated.length} new BOM items created` : ''}${updatedBOMs.length > 0 ? `, ${updatedBOMs.length} BOM items updated` : ''}`,
      results: results,
      successful: successful,
      failed: failed,
      newBOMsCreated: newBOMsCreated,
      updatedBOMs: updatedBOMs,
      invoice: createdInvoice
    };

  } catch (error) {
    console.error("Error adding path inventory from OCR:", error);
    throw new Error(`Failed to add items to inventory: ${error.message}`);
  }
};

// Helper function to convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

// Helper function to attempt JSON recovery from truncated responses
const attemptJSONRecovery = (jsonText) => {
  try {
    // Check if the JSON is truncated
    if (!jsonText.trim().endsWith('}')) {
      // Find the last complete item in the items array
      const itemsMatch = jsonText.match(/"items":\s*\[([\s\S]*?)(?=\s*\])/);
      if (itemsMatch) {
        const itemsContent = itemsMatch[1];
        
        // Find the last complete item object
        const itemMatches = itemsContent.match(/\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g);
        if (itemMatches && itemMatches.length > 0) {
          // Get the last complete item
          const lastCompleteItem = itemMatches[itemMatches.length - 1];
          
          // Check if the last item is complete
          if (lastCompleteItem.includes('"discount"') && lastCompleteItem.endsWith('}')) {
            // Reconstruct the JSON with complete items only
            const completeItems = itemMatches.slice(0, -1); // Remove the potentially incomplete last item
            const reconstructedItems = completeItems.join(',');
            
            // Find the invoice metadata
            const invoiceNumberMatch = jsonText.match(/"invoiceNumber":\s*"([^"]*)"/);
            const invoiceDateMatch = jsonText.match(/"invoiceDate":\s*"([^"]*)"/);
            const supplierMatch = jsonText.match(/"supplier":\s*"([^"]*)"/);
            const totalAmountMatch = jsonText.match(/"totalAmount":\s*([0-9.]+)/);
            
            const recoveredJson = {
              invoiceNumber: invoiceNumberMatch ? invoiceNumberMatch[1] : null,
              invoiceDate: invoiceDateMatch ? invoiceDateMatch[1] : null,
              supplier: supplierMatch ? supplierMatch[1] : null,
              totalAmount: totalAmountMatch ? parseFloat(totalAmountMatch[1]) : 0,
              items: completeItems.map(item => {
                try {
                  return JSON.parse(item);
                } catch (e) {
                  return null;
                }
              }).filter(item => item !== null)
            };
            
            return recoveredJson;
          }
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error("JSON recovery failed:", error);
    return null;
  }
};

const normalizeOcrUnitType = (unitType) => {
  const key = String(unitType || "")
    .trim()
    .toLowerCase();
  if (key === "box") return "Box";
  if (key === "bottle") return "Bottle";
  if (key === "packet") return "Packet";
  if (key === "each" || key === "piece" || key === "pieces" || key === "pc" || !key) {
    return "Each";
  }
  if (key === "ml") return "Bottle";
  if (key === "gm" || key === "g") return "Packet";
  return "Each";
};

const normalizeOcrQtyUnit = (unitType, qtyUnit) => {
  const explicit = String(qtyUnit || "")
    .trim()
    .toLowerCase();
  if (explicit === "ml") return "ml";
  if (explicit === "gm" || explicit === "g") return "gm";
  if (explicit === "piece" || explicit === "pieces" || explicit === "pc") return "piece";

  const key = String(unitType || "")
    .trim()
    .toLowerCase();
  if (key === "ml" || key === "bottle") return "ml";
  if (key === "gm" || key === "g" || key === "packet") return "gm";
  return "piece";
};

const validateAndCleanPathOCRData = (data) => {
  // Ensure items array exists
  if (!data.items || !Array.isArray(data.items)) {
    data.items = [];
  }

  // Clean and validate each item
  data.items = data.items.map(item => {
    // Clean item name - remove extra spaces and special characters
    const cleanName = (item.name || "Unknown Item").trim().replace(/\s+/g, ' ');
    
    // Handle quantity - try multiple field names (allow decimals like 0.5)
    const quantity = parseFloat(item.requiredQuantity || item.quantity || item.qty || 1);
    const requiredQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    
    // Handle cost/rate - try multiple field names
    const cost = parseFloat(item.cost || item.rate || item.price || 0) || 0;
    
    // Handle amount - try multiple field names
    const amount = parseFloat(item.amount || item.totalAmount || 0) || 0;
    
    // Handle batch number - clean it
    const batchNumber = item.batchNumber ? item.batchNumber.toString().trim() : null;
    
    // Handle expiry date - convert MM/YY to YYYY-MM-DD if needed
    let expiryDate = item.expiryDate;
    if (expiryDate && typeof expiryDate === 'string') {
      // Handle MM/YY format
      if (expiryDate.match(/^\d{2}\/\d{2}$/)) {
        const [month, year] = expiryDate.split('/');
        const fullYear = `20${year}`;
        expiryDate = `${fullYear}-${month.padStart(2, '0')}-30`; // Last day of month
      }
    }
    
    // Handle SGST/CGST - split combined values if needed
    let sgst = parseFloat(item.sgst || 0) || 0;
    let cgst = parseFloat(item.cgst || 0) || 0;
    
    // If we have a combined SGST+CGST field, try to split it
    if (item.sgstCgst || item.tax) {
      const combinedTax = (item.sgstCgst || item.tax).toString();
      if (combinedTax.includes('+')) {
        const parts = combinedTax.split('+');
        if (parts.length === 2) {
          sgst = parseFloat(parts[0].trim()) || 0;
          cgst = parseFloat(parts[1].trim()) || 0;
        }
      }
    }
    
    return {
      name: cleanName,
      unitType: normalizeOcrUnitType(item.unitType),
      qtyUnit: normalizeOcrQtyUnit(item.unitType, item.qtyUnit),
      details: parseFloat(item.details) > 0 ? parseFloat(item.details) : 1,
      requiredQuantity: requiredQuantity,
      cost: cost,
      inventoryQuantity: Number.isFinite(parseFloat(item.inventoryQuantity))
        ? Math.max(0, Math.trunc(parseFloat(item.inventoryQuantity)))
        : Math.max(1, Math.ceil(requiredQuantity)),
      batchNumber: batchNumber,
      expiryDate: expiryDate,
      amount: amount,
      sgst: sgst,
      cgst: cgst,
      discount: parseFloat(item.discount || 0) || 0,
      bomExists: false // Will be set later when checking against BOM table
    };
  });

  // Clean other fields
  return {
    invoiceNumber: data.invoiceNumber ? data.invoiceNumber.toString().trim() : null,
    invoiceDate: data.invoiceDate || null,
    supplier: data.supplier ? data.supplier.toString().trim() : null,
    totalAmount: parseFloat(data.totalAmount || 0) || 0,
    items: data.items
  };
};

const getMockPathOCRData = () => {
  return {
    invoiceNumber: "SA-01271",
    invoiceDate: "2025-08-26",
    supplier: "A.V. PHARMA",
    totalAmount: 12301.00,
    items: [
      {
        name: "PARACETAMOL TAB LOOSE",
        unitType: "pieces",
        requiredQuantity: 20,
        cost: 35.00,
        inventoryQuantity: 20,
        batchNumber: "T4354",
        expiryDate: "2027-04-30",
        amount: 700.00,
        sgst: 6.00,
        cgst: 6.00,
        discount: 0.00,
        bomExists: false
      },
      {
        name: "IBUPROFEN 400MG F/C",
        unitType: "pieces",
        requiredQuantity: 4,
        cost: 320.00,
        inventoryQuantity: 4,
        batchNumber: "60725",
        expiryDate: null,
        amount: 1280.00,
        sgst: 6.00,
        cgst: 6.00,
        discount: 0.00,
        bomExists: false
      },
      {
        name: "HISTAPHEN TAB",
        unitType: "pieces",
        requiredQuantity: 2,
        cost: 38.00,
        inventoryQuantity: 2,
        batchNumber: "T-8825",
        expiryDate: "2027-03-30",
        amount: 76.00,
        sgst: 6.00,
        cgst: 6.00,
        discount: 0.00,
        bomExists: false
      },
      {
        name: "MAGSULF 150GM IP (ANCHOR 150GM)",
        unitType: "pieces",
        requiredQuantity: 1,
        cost: 80.00,
        inventoryQuantity: 1,
        batchNumber: "92",
        expiryDate: null,
        amount: 80.00,
        sgst: 9.00,
        cgst: 9.00,
        discount: 0.00,
        bomExists: false
      }
    ]
  };
};
